import type { Adjustment } from '@/types/calendar'
import { paceBand, type PaceKey } from '@/lib/plain-language'

/**
 * Returns true when the adjustments array contains a non-zero target_pressure
 * stage entry. Used to decide whether to render the target indicator dot on
 * calendar cells. No math — reads the modifier_pct the backend stamped.
 */
export function hasTargetAdjustment(adjustments: Array<Partial<Adjustment>> | undefined): boolean {
  if (!adjustments || adjustments.length === 0) return false
  return adjustments.some(
    (a) => a.stage === 'target_pressure' && (a.modifier_pct ?? 0) !== 0
  )
}

/**
 * Canonical human-readable copy for each Target Pressure dormant reason (D7).
 * Single source of truth shared by the calendar hover card, the dashboard
 * outlook card, and the target detail page — keep these strings in one place so
 * the four dormant states never drift between surfaces. Pure display, no math.
 */
//
// Copy stays inside the locked Target vocabulary (docs/target-vocabulary.md § Dormant reason
// codes / Locked operator-facing copy patterns) — plain, but no new synonyms for the locked
// terms "projection confidence", "pickup", "target".
const DORMANT_REASON_LABELS: Record<string, string> = {
  no_pickup_history:
    'No pickup history yet — forecasting needs a month or two of bookings to learn the pattern',
  industry_default_preview_only:
    'Preview mode — using an industry baseline until this property has enough history',
  projection_confidence_below_threshold:
    'Projection confidence below threshold — engine standing by',
  no_pickup_remaining: 'No pickup remaining — this month is effectively set',
}

const DORMANT_REASON_FALLBACK = 'Target Pressure dormant — engine standing by'

/**
 * Map a backend dormant_reason code to its display string. Falls back to a
 * generic dormant message for null/unknown reasons (de-snake-casing as a last
 * resort so an unmapped code never renders raw).
 */
export function dormantReasonLabel(reason: string | null | undefined): string {
  if (!reason) return DORMANT_REASON_FALLBACK
  return DORMANT_REASON_LABELS[reason] ?? reason.replace(/_/g, ' ')
}

// ─── Booking velocity (D12 / D12-A) ─────────────────────────────────────────
//
// Locked vocabulary (docs/target-vocabulary.md, v3): operator label is "Booking velocity".
// PLAIN-LANGUAGE RULE (docs/revenue-vocabulary.md § Plain-language presentation): the bare
// multiplier (`0.8×`) NEVER appears on an operator surface — velocity presents on the same
// worded five-band scale as everything else (via `paceBand`). "on tempo" = on schedule; above
// = ahead of the curve; below = behind the curve. Never the word "pace" in Target-context copy.
// The raw ratio survives only inside `EngineDetails` on the target pages. These helpers only
// classify the value into worded copy — they never recompute the forecast number.

export type VelocityTone = 'hot' | 'neutral' | 'cold'

export interface VelocityDescriptor {
  tone: VelocityTone
  /**
   * Worded, curve-relative label — reads standalone in a chip ("behind the curve") AND after
   * the "Booking velocity " prefix screens hardcode ("Booking velocity behind the curve").
   * NEVER a bare multiplier.
   */
  display: string
  /** Short plain-scale companion, e.g. "slower than usual" — complements `display`, no repeat. */
  phrase: string
  /** Tailwind classes for the chip (matches the design-system semantic colors). */
  chipClass: string
  /** Hover-card explanatory copy, sourced from the locked vocabulary. */
  tooltip: string
  /**
   * Subtle relevance note (D12-A) when the trailing window observed only a small share of
   * the month's remaining pickup — velocity is "mostly silent at this horizon". null when
   * relevance is unknown (pre-v3) or high enough that velocity is genuinely speaking.
   */
  relevanceNote: string | null
}

// D12-A: when the trailing window covered a small share of remaining pickup, velocity is
// barely informative ("mostly silent at this horizon"). Surfaced subtly, not as a hard cutoff.
const VELOCITY_RELEVANCE_QUIET = 0.35

const VELOCITY_TOOLTIP_BASE =
  'Booking velocity compares this month’s recent bookings against what the booking curve normally ' +
  'brings in over the same window. On tempo means bookings are arriving as expected; ahead of the ' +
  'curve is faster than usual; behind the curve is slower. The projected end-of-month already reflects this.'

// paceBand key → { worded curve-relative label, plain-scale companion, chip tone }. Reuses the
// one worded five-band scale so velocity reads on the same words as demand/pace everywhere else.
const VELOCITY_COPY: Record<Exclude<PaceKey, 'unknown'>, { display: string; phrase: string; tone: VelocityTone }> = {
  well_ahead: { display: 'well ahead of the curve', phrase: 'much faster than usual', tone: 'hot' },
  ahead: { display: 'ahead of the curve', phrase: 'faster than usual', tone: 'hot' },
  on_track: { display: 'on tempo', phrase: 'right on schedule', tone: 'neutral' },
  behind: { display: 'behind the curve', phrase: 'slower than usual', tone: 'cold' },
  well_behind: { display: 'well behind the curve', phrase: 'much slower than usual', tone: 'cold' },
}

const VELOCITY_CHIP_CLASS: Record<VelocityTone, string> = {
  hot: 'bg-emerald-100 text-emerald-700',
  cold: 'bg-rose-100 text-rose-700',
  neutral: 'bg-slate-100 text-slate-600',
}

/**
 * Build the display descriptor for a booking-velocity ratio. Returns null when velocity is
 * absent (pre-v3 snapshots carry nil) so callers render nothing rather than a misleading 1.0.
 */
export function velocityDescriptor(
  ratio: number | null | undefined,
  relevance?: number | null,
): VelocityDescriptor | null {
  if (ratio == null) return null

  // Classify on the shared worded scale — same thresholds and band keys as every other pace
  // surface (on_track spans 0.95–1.05, the engine's neutral zone). No bare multiplier is built.
  const band = paceBand(ratio)
  const key: Exclude<PaceKey, 'unknown'> = band.key === 'unknown' ? 'on_track' : band.key
  const { display, phrase, tone } = VELOCITY_COPY[key]

  const relevanceNote =
    relevance != null && relevance < VELOCITY_RELEVANCE_QUIET
      ? 'Mostly silent at this horizon — the recent window has only seen a small share of this month’s remaining pickup, so the signal is weighted down.'
      : null

  return { tone, display, phrase, chipClass: VELOCITY_CHIP_CLASS[tone], tooltip: VELOCITY_TOOLTIP_BASE, relevanceNote }
}

// ─── Target row readouts (Targets page) ──────────────────────────────────────
//
// The locked labels (docs/target-vocabulary.md) stay as column headers and state badges —
// "Projected end-of-month", "Target gap", "Projection confidence", "Pushing"/"Easing"/"At
// floor"/"Dormant". These helpers produce everything an operator reads *next to* them: rule 2
// of the vocabulary lock says narrations are prose, so each row carries a sentence a revenue
// manager can act on rather than a number they have to decode.

/** The subset of a month's forecast snapshot these readouts need. */
export interface TargetRowForecast {
  state: string | null
  forecastEom: number | null
  forecastP10: number | null
  forecastP90: number | null
  atFloorPct: number | null
  dormantReason: string | null
  computedAt: string | null
}

/**
 * "19 pts short" / "6 pts ahead" / "On target" — the target gap, said out loud. Rounded to whole
 * occupancy points for the label (`18.97 pts short` is a readout, not an answer); the exact
 * figure the backend sent is untouched and still on the month's breakdown page.
 */
export function gapPhrase(gap: number | null | undefined): string {
  if (gap == null || !Number.isFinite(gap)) return '—'
  const rounded = Math.round(Math.abs(gap))
  if (rounded === 0) return 'On target'
  const pts = `${rounded} pts`
  return gap > 0 ? `${pts} ahead` : `${pts} short`
}

/**
 * "Likely 73–91%" — the forecast band, which is what a target should really be judged against.
 *
 * The band is widened to contain the projection. Forecast calibration moves `forecast_eom` and
 * the p10/p90 endpoints separately, so the projection can sit outside its own band (3 of 26 live
 * month-snapshots on 2026-07-27; Sachi July projected 76.0% against a 41–69% band). Rendering
 * both untouched puts a self-contradiction on the row — "76.0%" above "Likely 41–69%". This
 * mirrors exactly what `ResolveTargetVerdictMutation#best_reachable` already does when it picks
 * a recommended target, so the row and the recommendation can never disagree.
 */
export function bandPhrase(f: Pick<TargetRowForecast, 'forecastEom' | 'forecastP10' | 'forecastP90'>): string | null {
  if (f.forecastP10 == null || f.forecastP90 == null) return null
  const eom = f.forecastEom
  const low = eom != null ? Math.min(f.forecastP10, eom) : f.forecastP10
  const high = eom != null ? Math.max(f.forecastP90, eom) : f.forecastP90
  return `Likely ${low.toFixed(0)}–${high.toFixed(0)}%`
}

/**
 * The tone of the target-gap readout.
 *
 * `quiet` exists because the engine's own state machine already rules on whether a gap is worth
 * reacting to. A month in `on_track` has a gap INSIDE its noise floor — the engine has said "this
 * is forecast noise, do nothing" — so dressing that gap in rose (or emerald) contradicts the
 * "On track" badge and the "On target. Nothing to do." sentence sitting beside it. Same rule as
 * the verdict state gate, one column over: the comms layer never contradicts the state machine.
 */
export type GapTone = 'ahead' | 'short' | 'quiet'

/**
 * Classify the target gap's tone. Pure display classification — the gap itself is the backend's
 * number and is never recomputed here. The rounding mirrors `gapPhrase` exactly so the colour and
 * the words can never disagree ("On target" is never rose).
 */
export function gapTone(gap: number | null | undefined, state: string | null | undefined): GapTone {
  if (gap == null || !Number.isFinite(gap)) return 'quiet'
  if (state === 'on_track') return 'quiet'
  if (Math.round(Math.abs(gap)) === 0) return 'quiet'
  return gap > 0 ? 'ahead' : 'short'
}

export const GAP_TONE_CLASS: Record<GapTone, string> = {
  ahead: 'text-emerald-600',
  short: 'text-rose-600',
  quiet: 'text-muted-foreground',
}

// ─── Band at entry time (T4.2/T4.3) ──────────────────────────────────────────
//
// Guidance while the operator types, not a warning two hours after they commit. It is never a
// block or a validation error — the locked stance is that we never refuse a target (vocabulary
// § Verdict usage rules, rule 5). The operator types whatever they want; this only tells them
// what the number means.
//
// EVERYTHING numeric here comes from the backend. `position` is classified server-side in
// PreviewTargetImpactMutation#forecast_context_for against the band it also sends (widened to
// contain the projection, the same rule `bandPhrase` above uses on the row two columns away), so
// this module maps a classification to a sentence and interpolates two numbers. No comparisons,
// no rounding, no arithmetic on forecast values.

export type BandPosition = 'above_band' | 'within_band' | 'below_band'

/** One drafted month's entry (`forecast_context[month]`) from the preview response. */
export interface ForecastContext {
  band_low: number
  band_high: number
  projected: number | null
  position: BandPosition
}

export interface EntryGuidance {
  text: string
  tone: 'warn' | 'muted'
  className: string
}

/**
 * The one line under the Target input. Reads as help — it states where the number lands and what
 * the engine will do with it, and stops. Returns null when the month has no band, because a
 * month whose forecast cannot be bounded has nothing honest to say.
 */
export function entryGuidance(ctx: ForecastContext | null | undefined): EntryGuidance | null {
  if (!ctx) return null

  // "likely range" deliberately echoes the row's own band readout ("Likely 66–84%") so the
  // operator reads the two as one fact, not two.
  const range = `${ctx.band_low}–${ctx.band_high}%`

  switch (ctx.position) {
    case 'above_band':
      return {
        tone: 'warn',
        className: 'text-amber-600',
        text: `Above the likely range (${range}) — the engine will discount hard to chase it.`,
      }
    case 'below_band':
      return {
        tone: 'muted',
        className: 'text-muted-foreground',
        text: `Below the likely range (${range}) — the engine will hold rates up rather than chase.`,
      }
    case 'within_band':
      return {
        tone: 'muted',
        className: 'text-muted-foreground',
        text: `Inside the likely range (${range}) — the engine can steer to this.`,
      }
    default:
      return null
  }
}

/**
 * Projection confidence as words. The percentage is the locked readout and stays in the
 * tooltip; the chip says whether the number is worth leaning on.
 */
export function confidenceBand(c: number | null | undefined): { label: string; className: string } | null {
  if (c == null || !Number.isFinite(c)) return null
  if (c >= 0.7) return { label: 'Confident', className: 'bg-emerald-100 text-emerald-700' }
  if (c >= 0.3) return { label: 'Fairly confident', className: 'bg-amber-100 text-amber-700' }
  return { label: 'Still learning', className: 'bg-slate-100 text-slate-600' }
}

/** What the engine is doing about this month, in one sentence. */
export function engineNarration(f: TargetRowForecast): string {
  if (f.computedAt == null) return 'Waiting for the first calculation — this runs within the hour.'
  if (f.state === 'dormant') return dormantReasonLabel(f.dormantReason)
  if (f.state === 'at_floor') {
    const share = f.atFloorPct != null ? ` (${Math.round(f.atFloorPct * 100)}% of dates)` : ''
    return `Wants to discount further, but rates are already at your floor${share}.`
  }
  if (f.state === 'pushing') return 'Discounting to pull bookings in and close the gap.'
  if (f.state === 'easing') return 'You’re ahead — raising rates to earn more on what’s left.'
  if (f.state === 'on_track') return 'On target. Nothing to do.'
  return 'Standing by.'
}
